import { useMutation } from '@apollo/client/react';
import UndoIcon from '@mui/icons-material/Undo';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Button, Stack, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { round } from 'lodash-es';
import Link from 'next/link';
import { useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Brand } from '@calo/types';

import ActionButtons from './ActionButtons';
import ActualCookedMobileCluster from './ActualCookedMobileCluster';
import { AppContext } from '../../../../App';
import { caloTheme } from '../../../../assets/themes/calo';
import Modal from '../../../../components/Modal';
import { CookingStationName, CookingStations, FoodComponent, FoodComponentStationTitle } from '../../../../libs';
import { UPDATE_ACTUAL_COOKED_WEIGHT_V2_MUTATION, VERIFY_COMPONENT_V2_MUTATION } from '../../../../libs/graphQL';
import { getTableAttributes, getUserAttributes } from '../../../../libs/utils';
import { ComponentService } from '../../../../libs/utils/componentService';
import { getActualCookedWeightStation } from '../../../../libs/utils/foodComponentHelper';
import { calculateWeightChange } from '../../../../libs/utils/weightChangeHelper';
import ComponentProcessTime from '../../ComponentProcessTime/ComponentProcessTime';

interface CombinedShortages {
  ids: string[];
  totalWeight: number;
  cookingStations: CookingStations[];
}

interface SectionTableRowProps {
  statusAttributes: {
    isCheckable: boolean;
    lastUncheckedStation: string;
  };
  isChecked: boolean;
  foodComponent: FoodComponent;
  selectedComponentStation: CookingStationName;
  setSelectedComponentStation: (value: CookingStationName) => void;
  combinedShortages?: CombinedShortages;
  highlight?: boolean;
  onHighlightEnd?: () => void;
  showAllUnits: boolean;
  selectedHours: string;
}

const MobileSectionTableRow = ({
  showAllUnits,
  statusAttributes,
  foodComponent,
  isChecked,
  selectedComponentStation,
  setSelectedComponentStation,
  combinedShortages,
  highlight = false,
  selectedHours
}: SectionTableRowProps) => {
  const [isCheckedState, setIsCheckedState] = useState(isChecked);
  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState(false);

  const [mutateFoodComponent, { loading }] = useMutation(VERIFY_COMPONENT_V2_MUTATION);
  const [updateActualCookedWeight, { loading: updatingActual }] = useMutation(UPDATE_ACTUAL_COOKED_WEIGHT_V2_MUTATION);

  const theme = useTheme();
  const tableAttributes = getTableAttributes(statusAttributes.lastUncheckedStation, theme);

  const currentStation = foodComponent.cookingStations?.find((station) => station.name === selectedComponentStation);

  const isLastStation = foodComponent.cookingStations?.[foodComponent.cookingStations?.length - 1] === currentStation;
  const appContext = useContext(AppContext);
  const userActor = getUserAttributes(appContext.user);

  const weightChangeData = useMemo(
    () => calculateWeightChange(foodComponent.currentCookedWeight?.hourlyData, selectedHours, foodComponent.totalCookedWeight),
    [foodComponent.currentCookedWeight?.hourlyData, selectedHours, foodComponent.totalCookedWeight]
  );

  const { canEditActualCooked, lastEditableStationTitle } = useMemo(() => {
    const lastStation = getActualCookedWeightStation(foodComponent);
    if (!lastStation) {
      return { canEditActualCooked: false, lastEditableStationTitle: '' };
    }

    return {
      canEditActualCooked: selectedComponentStation === lastStation,
      lastEditableStationTitle: getTableAttributes(lastStation, theme).name
    };
  }, [foodComponent, selectedComponentStation, theme]);
  const [isEditingActual, setIsEditingActual] = useState(false);
  const [newActualValue, setNewActualValue] = useState('');
  const [showEntries, setShowEntries] = useState(false);

  const actualCookedTotal = useMemo(() => {
    const entries = (foodComponent as any).actualCookedWeight as Array<{ quantity: number }> | undefined;
    if (!Array.isArray(entries)) return 0;
    return entries.reduce((sum, e) => sum + (Number(e?.quantity) || 0), 0);
  }, [foodComponent]);

  const displayedCookedWeight = useMemo(() => {
    return weightChangeData.hasData
      ? round(weightChangeData.latestHourWeight, 2)
      : round(foodComponent?.totalCookedWeight ?? 0, 2);
  }, [weightChangeData, foodComponent]);

  const shortageWastage = useMemo(() => {
    const base = Number(displayedCookedWeight);
    if (actualCookedTotal === 0) return { value: '', isShortage: false, isWastage: false };
    const diff = base - actualCookedTotal;
    return { value: `${round(Math.abs(diff), 2)}g`, isShortage: diff > 0, isWastage: diff < 0 };
  }, [displayedCookedWeight, actualCookedTotal]);

  const handleAddActual = async (quantity: number) => {
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Enter a valid positive number');
      return;
    }
    const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
    const nextArray = [
      ...existing.map((e: any) => ({
        quantity: Number(e.quantity),
        timestamp: e.timestamp,
        actor: e.actor as string,
        id: e.id as string
      })),
      { quantity, timestamp: new Date().toISOString(), actor: userActor.name }
    ];
    try {
      await updateActualCookedWeight({
        variables: { kitchen: foodComponent.kitchen, id: foodComponent.id, actualCookedWeight: nextArray }
      });
      setIsEditingActual(false);
      setNewActualValue('');
    } catch (error: any) {
      toast.error(error.message ?? 'Failed to update');
    }
  };

  const handleEditActualEntry = async (index: number, quantity: number) => {
    const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
    const nextArray = existing.map((e: any, i: number) => ({
      quantity: i === index ? Number(quantity) : Number(e.quantity),
      timestamp: e.timestamp,
      actor: e.actor as string,
      id: e.id as string
    }));
    await updateActualCookedWeight({
      variables: { kitchen: foodComponent.kitchen, id: foodComponent.id, actualCookedWeight: nextArray }
    });
  };

  const handleDeleteActualEntry = async (index: number) => {
    const existing = ((foodComponent as any).actualCookedWeight as Array<any>) ?? [];
    const nextArray = existing
      .filter((_: any, i: number) => i !== index)
      .map((e: any) => ({ quantity: Number(e.quantity), timestamp: e.timestamp, actor: e.actor as string, id: e.id as string }));
    await updateActualCookedWeight({
      variables: { kitchen: foodComponent.kitchen, id: foodComponent.id, actualCookedWeight: nextArray }
    });
  };
  const handleFoodComponentVerify = async (isVerified: boolean) => {
    const user = getUserAttributes(appContext.user);
    await mutateFoodComponent({
      variables: {
        input: {
          id: foodComponent.id,
          isVerified,
          verifiedBy: user
        },
        kitchen: foodComponent.kitchen
      },
      onError: (e) => {
        toast.error(e.message);
      }
    }).catch(() => undefined);
  };

  const actionButtons = () => {
    if (combinedShortages) {
      return (
        <ActionButtons
          foodComponent={foodComponent}
          statusAttributes={statusAttributes}
          tableName={selectedComponentStation}
          combinedShortages={combinedShortages}
          isChecked={isCheckedState}
          setIsCheckedState={setIsCheckedState}
        />
      );
    } else {
      return (
        <ActionButtons
          foodComponent={foodComponent}
          tableName={selectedComponentStation}
          statusAttributes={statusAttributes}
          combinedShortages={undefined}
        />
      );
    }
  };

  return (
    <TableRow
      sx={{
        transition: 'background 0.5s',
        background: highlight ? 'rgba(255, 235, 59, 0.5)' : undefined
      }}
    >
      <Stack
        sx={{
          borderBottom: '1px solid ' + theme.palette.neutral200,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          px: '8px',
          py: '16px',
          width: '100%'
        }}
      >
        <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Link
            style={{
              fontWeight: 400,
              fontFamily: caloTheme.typography.fontFamily,
              lineHeight: '19px',
              fontSize: '16px',
              marginLeft: '2px',
              textDecoration: 'none',
              color: statusAttributes.isCheckable || currentStation?.endTime ? theme.palette.black : theme.palette.neutral600
            }}
            href={`/recipe-guide-book?id=${foodComponent.id.split('#')[1]}`}
          >
            {foodComponent.name.en}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500
              }}
            >
              Total Raw Weight:{' '}
              {weightChangeData.hasData
                ? Math.round(weightChangeData.latestHourWeight * (foodComponent.cookedRawFactor ?? 1) * 100) / 100
                : Math.round((foodComponent.totalCookedWeight ?? 0) * (foodComponent.cookedRawFactor ?? 1) * 100) / 100}
              g
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                mt: 0.5
              }}
            >
              Total {showAllUnits ? 'Quantity' : 'Cooked Weight'}:{' '}
              {showAllUnits
                ? round(
                    ComponentService.calculateComponentQuantityFromWeight(
                      [...(foodComponent.cups ?? [])],
                      foodComponent.measurementUnit,
                      foodComponent.cookedWeight ?? 0,
                      weightChangeData.hasData
                        ? Math.round(weightChangeData.latestHourWeight * 100) / 100
                        : (foodComponent.totalCookedWeight ?? 0)
                    ),
                    6
                  )
                : round(
                    weightChangeData.hasData
                      ? Math.round(weightChangeData.latestHourWeight * 100) / 100
                      : (foodComponent.totalCookedWeight ?? 0),
                    2
                  )}{' '}
              {showAllUnits ? foodComponent.measurementUnit : 'g'}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                mt: 0.5
              }}
            >
              Changes from {selectedHours}
              {weightChangeData.hasData ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                  <Tooltip
                    title={`Compared from ${selectedHours}: ${Math.round(weightChangeData.comparedWeight * 100) / 100}g`}
                    arrow
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: weightChangeData.isIncrease
                          ? '#4CAF50' // Green for increase
                          : weightChangeData.isDecrease
                            ? '#F44336' // Red for decrease
                            : theme.palette.grey[500], // Neutral for same
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'help'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: 'white',
                          lineHeight: 1
                        }}
                      >
                        {weightChangeData.isIncrease ? '↑' : weightChangeData.isDecrease ? '↓' : '−'}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 500
                    }}
                  >
                    {Math.round(weightChangeData.difference * 100) / 100}g
                  </Typography>
                  {weightChangeData.comparedWeight > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: weightChangeData.isIncrease
                          ? '#4CAF50'
                          : weightChangeData.isDecrease
                            ? '#F44336'
                            : theme.palette.grey[500],
                        ml: 0.5
                      }}
                    >
                      ({weightChangeData.isIncrease ? '+' : weightChangeData.isDecrease ? '-' : ''}
                      {Math.round((weightChangeData.difference / weightChangeData.comparedWeight) * 10000) / 100}%)
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: theme.palette.neutral400
                  }}
                >
                  -
                </Typography>
              )}
            </Typography>
          </Link>
          {foodComponent.isVerified && foodComponent.verifiedBy && isLastStation && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontFamily: caloTheme.typography.fontFamily,
                lineHeight: '19px',
                fontSize: '14px',
                ml: '2px',
                color: theme.palette.grey[500]
              }}
            >
              Verified by {foodComponent.verifiedBy.name}
            </Typography>
          )}
          {!currentStation?.processTime && (
            <Typography
              sx={{
                borderRadius: '16px',
                padding: '8px',
                width: '120px',
                textAlign: 'center',
                bgcolor: tableAttributes.color,
                display: statusAttributes.lastUncheckedStation === selectedComponentStation ? 'none' : '',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '17px',
                color: caloTheme.palette.neutral900
              }}
              onClick={() => setSelectedComponentStation(statusAttributes.lastUncheckedStation as CookingStationName)}
            >
              {FoodComponentStationTitle[statusAttributes.lastUncheckedStation as keyof typeof FoodComponentStationTitle]}
            </Typography>
          )}
        </Stack>
        {canEditActualCooked ? (
          <ActualCookedMobileCluster
            foodComponent={foodComponent}
            updating={updatingActual}
            actualCookedTotal={actualCookedTotal}
            shortageWastage={shortageWastage}
            showEntries={showEntries}
            setShowEntries={setShowEntries}
            isEditing={isEditingActual}
            setIsEditing={setIsEditingActual}
            newValue={newActualValue}
            setNewValue={setNewActualValue}
            onAdd={handleAddActual}
            onEdit={handleEditActualEntry}
            onDelete={handleDeleteActualEntry}
          />
        ) : (
          lastEditableStationTitle && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: theme.palette.neutral600,
                mt: 0.5
              }}
            >
              Go to {lastEditableStationTitle} to enter actual cooked weight
            </Typography>
          )
        )}
        {foodComponent.brand !== Brand.CALO && (
          <Typography variant="h4" sx={{ mr: 1, fontWeight: 400, fontSize: '23px', lineHeight: '28px', color: '#FF9900' }}>
            M
          </Typography>
        )}

        <Stack
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
        >
          {currentStation && currentStation.processTime && !combinedShortages && (
            <ComponentProcessTime
              combinedShortages={combinedShortages}
              foodComponent={foodComponent}
              processTime={currentStation.processTime}
              endTime={currentStation.endTime ?? ''}
            />
          )}
          {statusAttributes.isCheckable || currentStation?.endTime ? (
            (currentStation && !currentStation.endTime) || combinedShortages ? (
              actionButtons()
            ) : (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center'
                }}
              >
                {isLastStation && (
                  <>
                    {foodComponent.isVerified ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginRight: '20px'
                        }}
                      >
                        <VerifiedIcon />
                        <Typography
                          sx={{
                            fontWeight: '500',
                            fontSize: '13px',
                            marginLeft: '5px'
                          }}
                        >
                          VERIFIED
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        disabled={loading}
                        onClick={() => void handleFoodComponentVerify(true)}
                        startIcon={<VerifiedIcon />}
                        sx={{
                          color: theme.palette.black,
                          backgroundColor: theme.palette.white,
                          borderColor: theme.palette.black,
                          borderRadius: '5px',
                          textTransform: 'capitalize'
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: '500',
                            fontSize: '13px'
                          }}
                        >
                          VERIFY
                        </Typography>
                      </Button>
                    )}
                  </>
                )}
                {foodComponent.isVerified && isLastStation ? (
                  <Button
                    variant="outlined"
                    disabled={!statusAttributes.isCheckable || loading || appContext.isOffline}
                    onClick={() => setIsConfirmationModalOpened(true)}
                    style={{ width: '32px', minWidth: '32px' }}
                    sx={{
                      backgroundColor: theme.palette.white,
                      borderColor: theme.palette.black,
                      borderRadius: '5px',
                      padding: 0,
                      height: '32px',
                      '&:disabled': {
                        backgroundColor: theme.palette.white
                      }
                    }}
                  >
                    <UndoIcon sx={{ color: theme.palette.black }} />
                  </Button>
                ) : (
                  <Typography sx={{ color: theme.palette.black, mr: '20px', fontWeight: 600, fontSize: '13px', ml: '20px' }}>
                    COMPLETED
                  </Typography>
                )}
              </Box>
            )
          ) : (
            <Typography sx={{ color: theme.palette.neutral600, fontWeight: 600, mr: 3, width: '100%', textAlign: 'center' }}>
              Pending
            </Typography>
          )}
        </Stack>
        {combinedShortages && (
          <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 400, fontSize: '14px', color: caloTheme.palette.neutral900 }}>
              Quantity: {combinedShortages?.totalWeight ?? 0} g
            </Typography>

            <Typography sx={{ fontWeight: 400, fontSize: '10px', color: caloTheme.palette.red }}>Shortage</Typography>
          </Stack>
        )}
      </Stack>
      <Modal
        loading={loading}
        open={isConfirmationModalOpened}
        setOpen={(isOpened: boolean) => setIsConfirmationModalOpened(isOpened)}
        handleClose={() => setIsConfirmationModalOpened(false)}
        handleAccept={() => void handleFoodComponentVerify(false)}
        title={'Undo confirmation '}
        message={'Are you sure you want to undo this action?'}
      />
    </TableRow>
  );
};

export default MobileSectionTableRow;
